// English strings for the privacy values stream: retroactive data minimisation (PB-16-03, B-17,
// PD-610 – PD-616), the collection-matrix publish confirmation (B-31, PD-615), the legal basis
// catalogue and per-value privacy settings (H-04 / B-14, PD-617 – PD-621). Arabic: ar-priv.ts.
export const enPriv = {
  // ---------------------------------------------------------------- API messages
  'priv.api.impactChanged': 'The records affected changed after the confirmation was shown. Review the impact again, then publish.',
  'priv.api.basisNeedsNotice': 'Some purposes that cite this basis have no consent notice. Add a notice to each of them before this basis requires consent.',
  'priv.api.basisInUse': 'Purposes still cite this basis. Change or retire those purposes first.',
  'priv.v.consentBasis': 'The consent basis always requires a consent notice.',
  'priv.v.basisUnknown': 'Choose an active legal basis from the catalogue.',

  // ---------------------------------------------------------------- publish confirmation (B-31)
  'priv.pub.title': 'Publish collection rules version {n}?',
  'priv.pub.subtitle': 'Check what changes for visitors before it takes effect.',
  'priv.pub.body': 'Publishing changes what visitors may be asked from now on. Values already collected that the new rules do not allow are hidden at once everywhere — screens, searches, reports and exports. Hidden values come back if a later version allows them again, unless they were purged.',
  'priv.pub.types': 'Visitor types affected',
  'priv.pub.noTypes': 'No visitor type changes.',
  'priv.pub.items': 'Fields and documents affected',
  'priv.pub.change': 'Change',
  'priv.pub.noChanges': 'No field changes for any visitor type.',
  'priv.pub.records': 'Existing records',
  'priv.pub.hide': { one: '{count} existing value will be hidden', other: '{count} existing values will be hidden' },
  'priv.pub.hideHow': 'Report and audit export files and subject exports prepared before now are withdrawn. A privacy administrator can then plan a reviewed purge of the hidden values.',
  'priv.pub.hideNone': 'No existing value becomes hidden.',
  'priv.pub.restore': { one: '{count} value hidden earlier will be shown again.', other: '{count} values hidden earlier will be shown again.' },
  'priv.pub.ack': { one: 'I understand that {count} existing value will be hidden.', other: 'I understand that {count} existing values will be hidden.' },

  // ---------------------------------------------------------------- hidden values (B-17)
  'priv.hid.title': 'Values hidden by the collection rules',
  'priv.hid.intro': 'Values collected before a rule disallowed them. Nobody can see them; they are kept only until a reviewed purge or until the rule allows them again.',
  'priv.hid.empty': 'No values are hidden.',
  'priv.hid.total': { one: '{count} hidden value', other: '{count} hidden values' },
  'priv.hid.record': 'Record',
  'priv.hid.since': 'Hidden since',
  'priv.hid.restorable': 'If the rule is relaxed',
  'priv.hid.byVersion': 'By rules version {n}',
  'priv.hid.canReturn': 'Comes back',
  'priv.hid.newerValue': 'Newer value collected',
  'priv.hid.more': 'Only the first 200 are listed.',
  'priv.hid.purgeItem': 'Values to purge',
  'priv.hid.allItems': 'All hidden values',
  'priv.hid.plan': 'Plan reviewed purge',
  'priv.hid.planned': 'Purge planned. A privacy authority reviews and applies it under Purge jobs.',
  'priv.hid.fourEyes': 'The person who plans a purge cannot apply it. Applying it cannot be undone.',
  'priv.target.person_email': 'Email on profile',
  'priv.target.person_phone': 'Mobile number on profile',
  'priv.target.person_organisation': 'Organisation on profile',
  'priv.target.participation_organisation': 'Organisation on a visit',
  'priv.target.profile_field': 'Profile field',
  'priv.target.identity_number': 'Identity document number',
  'priv.target.identity_image': 'Identity document image',
  'priv.target.visitor_document': 'Pre-registration document',
  'priv.run.field': 'Field: {field}',
  'pv.pg.kind.minimisation': 'Purge of hidden values',
  'pv.exception.no_longer_hidden': 'Shown again by later rules',

  // ---------------------------------------------------------------- legal bases (B-14)
  'priv.lb.title': 'Legal bases',
  'priv.lb.intro': 'The lawful bases purposes may cite, with your organisation’s wording in English and Arabic. Each change is kept as a version.',
  'priv.lb.add': 'Add legal basis',
  'priv.lb.wording': 'Wording',
  'priv.lb.consent': 'Needs consent',
  'priv.lb.noWording': 'Not written yet',
  'priv.lb.active': 'Active',
  'priv.lb.retired': 'Retired',
  'priv.lb.version': 'Version {n}',
  'priv.lb.usedBy': { one: 'Cited by {count} purpose', other: 'Cited by {count} purposes' },
  'priv.lb.edit': 'Edit',
  'priv.lb.retire': 'Retire',
  'priv.lb.noInvent': 'Passage ships placeholder bases only. The bases and their wording come from your legal review; nothing here claims compliance.',
  'priv.lb.editTitle': 'Legal basis {code}',
  'priv.lb.dialogHint': 'Saving creates a new version. Purposes keep citing the basis by its code.',
  'priv.lb.code': 'Code',
  'priv.lb.codeHint': 'Lowercase letters and underscores, for example statutory_duty.',
  'priv.lb.labelEn': 'Name (English)',
  'priv.lb.labelAr': 'Name (Arabic)',
  'priv.lb.wordingEn': 'Legal wording (English)',
  'priv.lb.wordingAr': 'Legal wording (Arabic)',
  'priv.lb.noteHint': 'Optional: why it changed, for example the review reference.',
  'priv.lb.requiresConsent': 'Processing on this basis needs a recorded consent notice',
  'priv.lb.retireTitle': 'Retire legal basis {code}?',
  'priv.lb.retireBody': 'No purpose cites it. It stays in the history and can be saved again later.',

  // ---------------------------------------------------------------- settings (B-14)
  'priv.set.intro': 'Each value shows whether it is still the engineering default. The legal values are an owner decision; saving changes only the values you edited.',
  'priv.set.organisation': 'Set by your organisation',
  'priv.set.reset': 'Use engineering default',
  'priv.set.reportExportHours': 'Report and audit export download window (hours)',
  'priv.set.reportExportHint': 'Empty: the installation default of {hours} hours.',
  'priv.set.saveHint': 'Only changed values are saved.',
} as const;
